import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AddOutlined,
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  ExpandMore,
  PauseCircleOutlineOutlined,
  PlayCircleOutlineOutlined,
  RefreshOutlined,
  SearchOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import StatusChip from "components/shared/StatusChip/StatusChip";
import { isCrossOrgAdminRole } from "components/RequirePermission/RequireEcrOpsRole";
import { usePermission, useCurrentUserRoleNames } from "components/RequirePermission/useAccessPermissions";
import { useGetRoleAssignmentsQuery, useGetRolesQuery } from "state/rtkQuery/access/accessQueries";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { PERMISSIONS } from "utils/accessManagement/permissionKeys";
import {
  buildOrgsById,
  buildRolesById,
  getAssigneeDisplay,
  getAssignmentTypeLabel,
  getOrganizationDisplay,
  resolveAssignmentRole,
} from "utils/accessManagement/roleAssignmentDisplay";
import { canManageRole, getMyMaxRolePriority, isDefaultUserRole } from "utils/accessManagement/roleAssignmentForm";
import { generateRandomColor } from "utils/generateRandomColor";
import { showToast } from "utils/reusableToast";
import { useDebounce } from "utils/useDebounce";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";

import AssignRoleDrawer from "./components/AssignRoleDrawer";
import DeactivateConfirmDialog from "./components/DeactivateConfirmDialog";
import DeleteAssignmentDialog from "./components/DeleteAssignmentDialog";
import EditAssignmentDrawer from "./components/EditAssignmentDrawer";

import "./AccessManagementLayout.css";

// The backend paginates the underlying assignment ROWS first, then groups
// within that page's slice — it does not paginate by group count. A small
// per_page can land almost entirely inside one prolific user's assignments,
// hiding everyone else. Request a much larger slice so a representative set
// of groups renders, matching the "just get everything reasonable"
// per_page=200 convention already used elsewhere (OrgAccessPage, useGetRolesQuery).
const GROUPED_PER_PAGE = 200;

// No name field exists anywhere in this API (only email/user_id/oid) — the
// avatar's letters come from the email's own local-part rather than a real
// name split, e.g. "keidsouza@deloitte.com" -> "KE". An app group's key is
// its app_id, not an email, so this only ever applies to user groups.
const emailInitials = (email) => (email || "").split("@")[0].replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase() || "?";

// Sub-tabs replace the old "Group by" dropdown — every view is grouped, so
// there's no "None"/flat option to offer. "Users" answers "what does this one
// person hold" (group_by=email); "Roles" answers "who holds this role"
// (group_by=role).
const VIEWS = [
  { value: "users", label: "Users", groupBy: "email" },
  { value: "roles", label: "Roles", groupBy: "role" },
];

// Colored circle seeded by a stable string (email, or app_id for an
// application group) — same convention as JoinedTab's AvatarRandomColor,
// just inline/compact for a table cell instead of that component's fixed
// 40px sizing.
const GroupAvatar = ({ seed, initials }) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: "#fff",
      fontSize: "12px",
      fontWeight: 700,
      backgroundColor: generateRandomColor(seed || "?"),
    }}
  >
    {initials}
  </Box>
);

const RoleAssignmentsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValueParam = searchParams.get("search_value") || "";
  const pageParam = Number(searchParams.get("page")) || 1;
  const viewParam = VIEWS.some((v) => v.value === searchParams.get("view")) ? searchParams.get("view") : "users";
  const groupBy = VIEWS.find((v) => v.value === viewParam).groupBy;

  const [searchInput, setSearchInput] = useState(searchValueParam);
  const debouncedSearchInput = useDebounce(searchInput, 400);
  // Users-tab list only — sort_by is "email" or "assigned_by" per the API,
  // neither of which is meaningful for the Roles tab's role-name grouping.
  // Two-state toggle (asc <-> desc, no "off" state) rather than the
  // three-state clearable pattern used elsewhere — email sort is always on.
  const [sortOrder, setSortOrder] = useState("asc");
  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  };
  const [typeFilter, setTypeFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  // Stores only the group's key, not a snapshot of the group object itself —
  // the displayed group is looked up fresh from `groups` (below) on every
  // render, so reactivating/deactivating/deleting an assignment from inside
  // the modal is reflected immediately once the mutation invalidates the
  // RoleAssignment cache tag and the query refetches, without needing to
  // close and reopen the modal.
  const [openGroupKey, setOpenGroupKey] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialEmail, setCreateInitialEmail] = useState("");
  const [createInitialRoleId, setCreateInitialRoleId] = useState("");
  // Set only when the Assign role drawer was opened FROM a group modal (via
  // handleAssignFromGroup) — null when opened from the page-level "Assign
  // role" button, which has no originating modal to return to. Restored into
  // openGroupKey when the drawer closes, either way (save or cancel), so
  // "Assign role" from inside a user's modal returns to that same modal
  // instead of dropping back to the plain table.
  const [returnToGroupKey, setReturnToGroupKey] = useState(null);
  // Stores only the assignment's id, not a snapshot — looked up fresh from
  // the open group's live role_assignments on every render, same reasoning
  // as openGroupKey above.
  const [detailAssignmentId, setDetailAssignmentId] = useState(null);
  const [editAssignment, setEditAssignment] = useState(null);
  const [deactivateAssignment, setDeactivateAssignment] = useState(null);
  const [deleteAssignment, setDeleteAssignment] = useState(null);

  const canRead = usePermission(PERMISSIONS.ROLE_ASSIGNMENTS_READ);
  const canCreate = usePermission(PERMISSIONS.ROLE_ASSIGNMENTS_CREATE);
  const canUpdate = usePermission(PERMISSIONS.ROLE_ASSIGNMENTS_UPDATE);
  const canDelete = usePermission(PERMISSIONS.ROLE_ASSIGNMENTS_DELETE);

  const {
    data: assignmentsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetRoleAssignmentsQuery(
    {
      page: pageParam,
      perPage: GROUPED_PER_PAGE,
      email: debouncedSearchInput.trim() || undefined,
      roleId: roleFilter !== "all" ? roleFilter : undefined,
      isApp: typeFilter === "all" ? undefined : typeFilter === "application",
      isActive: statusFilter === "all" ? undefined : statusFilter === "active",
      groupBy,
      sortBy: groupBy === "email" ? "email" : undefined,
      sortOrder: groupBy === "email" ? sortOrder : undefined,
    },
    { skip: !canRead }
  );

  // Switching tabs changes groupBy (and therefore which columns render —
  // e.g. the Users tab's "User" column expects an email) on the same render
  // as the new query args, but RTK Query keeps serving the *previous* tab's
  // still-cached `assignmentsData` (role IDs, if coming from the Roles tab)
  // until the new fetch resolves — briefly rendering one tab's columns
  // against the other tab's row data. Track which groupBy the currently-held
  // data actually belongs to, and treat a fetch-in-progress for a *different*
  // groupBy as still-loading rather than rendering the mismatch.
  const lastRenderedGroupBy = useRef(groupBy);
  useEffect(() => {
    if (!isFetching) lastRenderedGroupBy.current = groupBy;
  }, [isFetching, groupBy]);
  const isGroupByMismatched = isFetching && lastRenderedGroupBy.current !== groupBy;

  const { data: rolesData } = useGetRolesQuery({ perPage: 200 }, { skip: !canRead });
  const rolesById = useMemo(() => buildRolesById(rolesData?.roles), [rolesData]);
  // "User" is the default role every account gets automatically — never
  // something an admin picks to assign, so it's excluded from both drawers'
  // role pickers (existing assignments to it, if any, are still shown/edited
  // normally elsewhere on this page — only the CREATE/CHANGE-TO picker hides it).
  const assignableRoles = useMemo(
    () => (rolesData?.roles || []).filter((role) => !isDefaultUserRole(role.role_name)),
    [rolesData]
  );
  // Role hierarchy: you can only edit/activate/deactivate/delete an
  // assignment whose role is at or below your own highest rank — see
  // AssignRoleDrawer/EditAssignmentDrawer for the same rule applied to
  // creating/switching a role, and getMyMaxRolePriority's own comment for
  // why this compares priority numbers rather than hardcoded role names.
  const myRoleNames = useCurrentUserRoleNames();
  const myMaxPriority = useMemo(
    () => getMyMaxRolePriority(myRoleNames, rolesData?.roles),
    [myRoleNames, rolesData]
  );

  const { data: activeOrgs } = useGetActiveOrganizationsQuery(undefined, { skip: !canRead });
  const orgsById = useMemo(() => buildOrgsById(activeOrgs), [activeOrgs]);

  const setPage = useCallback(
    (nextPage) => {
      const next = new URLSearchParams(searchParams);
      next.set("page", String(nextPage));
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  // Search-as-you-type: once the debounced value settles, mirror it into the
  // URL (so a search is shareable/deep-linkable and survives a refresh) and
  // reset back to page 1 — skipping the very first render so simply loading
  // the page with an existing ?search_value= doesn't immediately reset it.
  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      next.set("search_value", debouncedSearchInput.trim());
    } else {
      next.delete("search_value");
    }
    next.set("page", "1");
    setSearchParams(next);
  }, [debouncedSearchInput]); // eslint-disable-line

  const handleClearFilters = () => {
    setSearchInput("");
    setTypeFilter("all");
    setRoleFilter("all");
    setStatusFilter("all");
    setSearchParams({ view: viewParam });
  };

  const hasActiveFilters =
    typeFilter !== "all" || roleFilter !== "all" || statusFilter !== "all" || !!debouncedSearchInput.trim();

  const groups = assignmentsData?.groups || [];

  // Per-group summary for the list row — computed from the assignments
  // already included in each group (no extra fetch): whether the person
  // holds any cross-org role (Enterprise Admin, Operations Admin/User),
  // and an active/inactive breakdown when the group isn't all-active.
  const groupSummaries = useMemo(() => {
    const summaries = new Map();
    groups.forEach((group) => {
      const rows = group.role_assignments || [];
      const activeCount = rows.filter((r) => r.is_active).length;
      const hasCrossOrgRole = rows.some((r) => {
        const role = rolesById.get(r.role_id);
        return role && isCrossOrgAdminRole([role.role_name]);
      });
      summaries.set(String(group.group), {
        activeCount,
        inactiveCount: rows.length - activeCount,
        hasCrossOrgRole,
      });
    });
    return summaries;
  }, [groups, rolesById]);

  const assignmentsCountCell = (params) => {
    const summary = groupSummaries.get(params.id);
    return (
      <span style={{ fontSize: "12px", color: "#9ca3af" }}>
        {params.row.group.role_assignments.length} assignment{params.row.group.role_assignments.length === 1 ? "" : "s"}
        {summary?.inactiveCount > 0 && (
          <span style={{ color: "#b45309" }}> · {summary.inactiveCount} inactive</span>
        )}
      </span>
    );
  };

  const expandActionCell = () => (
    <Button size="small" sx={{ textTransform: "none", fontSize: "12px" }}>
      Expand
    </Button>
  );

  const userListColumns = useMemo(
    () => [
      {
        field: "user",
        headerName: "Email",
        flex: 1.6,
        minWidth: 220,
        sortable: false,
        renderHeader: () => (
          <Box
            component="button"
            type="button"
            onClick={toggleSortOrder}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
              font: "inherit",
              color: "inherit",
            }}
          >
            <span>Email</span>
            {sortOrder === "asc" ? (
              <ArrowUpwardOutlined sx={{ fontSize: 15 }} />
            ) : (
              <ArrowDownwardOutlined sx={{ fontSize: 15 }} />
            )}
          </Box>
        ),
        renderCell: (params) => {
          const email = params.row.group.group;
          const isAppGroup = params.row.group.role_assignments?.[0]?.is_app;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <GroupAvatar seed={email} initials={isAppGroup ? "AP" : emailInitials(email)} />
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{email}</span>
            </Box>
          );
        },
      },
      {
        field: "access",
        headerName: "Access",
        width: 130,
        sortable: false,
        renderCell: (params) => {
          const summary = groupSummaries.get(params.id);
          if (!summary?.hasCrossOrgRole) return null;
          return (
            <Chip
              icon={<ShieldOutlined sx={{ fontSize: "14px !important", color: "#86BC25 !important" }} />}
              label="Cross-org"
              size="small"
              sx={{ fontSize: "11px", fontWeight: 700, background: "#EEF7E1", color: "#4d7a15" }}
            />
          );
        },
      },
      {
        field: "assignments",
        headerName: "Assignments",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: assignmentsCountCell,
      },
      {
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: expandActionCell,
      },
    ],
    [groupSummaries] // eslint-disable-line
  );

  const roleListColumns = useMemo(
    () => [
      {
        field: "role",
        headerName: "Role",
        flex: 1.6,
        minWidth: 220,
        renderCell: (params) => {
          const group = params.row.group;
          const label = group.role_name || rolesById.get(group.group)?.role_name || group.group;
          return <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{label}</span>;
        },
      },
      {
        field: "assignments",
        headerName: "Assignments",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: assignmentsCountCell,
      },
      {
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: expandActionCell,
      },
    ],
    [rolesById] // eslint-disable-line
  );

  // The group detail modal fetches independently of the list's filters —
  // e.g. filtering the Users list by Role would otherwise mean a person's
  // modal only shows the one role that matched the filter, not everything
  // they hold. Keyed by email/role_id alone, ignoring type/status/search.
  const {
    data: openGroupData,
    isFetching: isOpenGroupFetching,
  } = useGetRoleAssignmentsQuery(
    {
      perPage: GROUPED_PER_PAGE,
      email: groupBy === "email" ? openGroupKey : undefined,
      roleId: groupBy === "role" ? openGroupKey : undefined,
      groupBy,
    },
    { skip: !canRead || !openGroupKey }
  );
  // isFetching (not isLoading) — isLoading is only true for this hook's very
  // first-ever fetch, so opening a *second* user's modal after the first one
  // already resolved would otherwise show that first user's still-cached
  // openGroupData while the new request is in flight, rather than a loading
  // state. Also guards on some returned group's own key actually matching
  // what's currently requested, since a fetch that's already in flight when
  // openGroupKey changes again can otherwise resolve into a stale render for
  // one commit before the newer request's result lands.
  //
  // Real bug: the backend's email filter can match on more than exact
  // equality (e.g. requesting "didarji@deloitte.com" also returned a
  // "usa-didarji@deloitte.com" group), so the response can contain more than
  // one group — and the extra group isn't guaranteed to sort after the exact
  // match. Matching only groups[0] meant an exact match landing at any other
  // index never satisfied this check, leaving the modal stuck loading
  // forever. Find the group by key instead of assuming its position.
  const matchedOpenGroup = openGroupData?.groups?.find((g) => String(g.group) === String(openGroupKey));
  const openGroupMatchesKey = Boolean(matchedOpenGroup);
  const isOpenGroupLoading = isOpenGroupFetching || !openGroupMatchesKey;
  const openGroup = openGroupKey && openGroupMatchesKey ? matchedOpenGroup : null;

  // Deleting a group's LAST assignment removes that group from the API
  // response entirely (deactivating/reactivating never does this — the
  // record stays, just marked (in)active, still visible with a status
  // badge). Without this, openGroupMatchesKey above would never become true
  // again after such a delete and the modal would show its loading spinner
  // forever instead of closing — the only case where auto-closing the modal
  // is correct UX, since there is nothing left to show.
  //
  // This is gated on pendingDeleteAssignedIdRef, set right before a delete
  // actually succeeds (see the DeleteAssignmentDialog below's onDeleted),
  // rather than inferred purely from "the group's key doesn't match right
  // now" — that inference was a real bug: reactivating a group's only
  // assignment could ALSO transiently report as not-matching (an RTK Query
  // refetch-timing edge case around the cache-invalidation window), which
  // closed the modal on a plain Reactivate/Deactivate/Edit with assignments
  // still very much remaining. Requiring an explicit "a delete for this
  // group's key just succeeded" signal removes that ambiguity entirely.
  const pendingDeleteGroupKeyRef = useRef(null);
  const groupEmptiedByDelete =
    Boolean(openGroupKey) &&
    pendingDeleteGroupKeyRef.current === openGroupKey &&
    !isOpenGroupFetching &&
    !openGroupMatchesKey &&
    openGroupData;
  useEffect(() => {
    if (!groupEmptiedByDelete) return;
    pendingDeleteGroupKeyRef.current = null;
    showToast({ title: "No assignments remaining", description: "This person/application has no remaining role assignments.", type: "info" });
    closeGroupModal();
  }, [groupEmptiedByDelete]);

  // Users-tab modal only: instead of one flat table of every assignment (a
  // prolific org owner can easily hit 30+ rows), split into a section per
  // role — so "what roles does this person hold" is answerable at a glance,
  // and expanding a section shows just that role's assignments. Cross-org
  // roles (Enterprise Admin, Operations Admin/User) are pinned to the top
  // regardless of how few assignments they carry — those are the roles most
  // likely to get missed by scrolling past dozens of per-org rows, which is
  // exactly the opposite of what a scan of this modal should surface first.
  const roleSections = useMemo(() => {
    if (groupBy !== "email" || !openGroup) return [];
    const byRoleId = new Map();
    openGroup.role_assignments.forEach((assignment) => {
      const { role, label, isUnknown } = resolveAssignmentRole(assignment, rolesById);
      const key = assignment.role_id || "unknown";
      if (!byRoleId.has(key)) {
        byRoleId.set(key, {
          roleId: key,
          label,
          isCrossOrg: !isUnknown && isCrossOrgAdminRole([role.role_name]),
          assignments: [],
        });
      }
      byRoleId.get(key).assignments.push(assignment);
    });
    return Array.from(byRoleId.values()).sort((a, b) => {
      if (a.isCrossOrg !== b.isCrossOrg) return a.isCrossOrg ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
  }, [groupBy, openGroup, rolesById]);

  // Auto-expand only the top section (a cross-org role first, if the user
  // holds one) so something is visible the instant the modal opens, instead
  // of every section starting collapsed with nothing to see. Re-derived
  // whenever a (possibly different) group modal opens.
  const [expandedRoleSection, setExpandedRoleSection] = useState(null);
  useEffect(() => {
    setExpandedRoleSection(roleSections[0]?.roleId ?? null);
  }, [openGroupKey]); // eslint-disable-line
  const detailAssignment = detailAssignmentId
    ? openGroup?.role_assignments?.find((a) => a.id === detailAssignmentId) || null
    : null;

  const total = assignmentsData?.total ?? 0;
  const effectivePerPage = assignmentsData?.per_page || GROUPED_PER_PAGE;
  const nPages = Math.max(1, Math.ceil(total / effectivePerPage));

  // Closes the group detail modal and opens the Assign role drawer, pre-filled
  // with whatever the modal already tells us: the person's email on the Users
  // tab (group_by=email — group IS the email, unless this is an application
  // group, which the API groups by app_id under the same key — never
  // pre-filled as an email), or the role on the Roles tab (group_by=role —
  // group IS the role_id).
  const handleAssignFromGroup = (group) => {
    const isAppGroup = group.role_assignments?.[0]?.is_app;
    setReturnToGroupKey(openGroupKey);
    setOpenGroupKey(null);
    setCreateInitialEmail(groupBy === "email" && !isAppGroup ? group.group : "");
    setCreateInitialRoleId(groupBy === "role" ? group.group : "");
    setCreateOpen(true);
  };

  // Closes the group modal and its nested assignment-detail modal together —
  // the detail modal has no meaning without its parent group open.
  const closeGroupModal = () => {
    setOpenGroupKey(null);
    setDetailAssignmentId(null);
  };

  const columns = useMemo(
    () => [
      {
        field: "assignee",
        headerName: "Assignee",
        flex: 1.6,
        minWidth: 200,
        renderCell: ({ row }) => {
          const { primary, secondary } = getAssigneeDisplay(row);
          return (
            <div style={{ lineHeight: 1.3, minWidth: 0 }}>
              <div style={{ fontSize: "13px", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {primary}
              </div>
              {secondary && (
                <div style={{ fontSize: "11px", color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {secondary}
                </div>
              )}
            </div>
          );
        },
      },
      {
        field: "is_app",
        headerName: "Type",
        width: 110,
        renderCell: ({ row }) => (
          <Chip
            label={getAssignmentTypeLabel(row)}
            size="small"
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              background: row.is_app ? "#e8f4fc" : "#f3f4f6",
              color: row.is_app ? "#0550ae" : "#374151",
            }}
          />
        ),
      },
      {
        field: "role_id",
        headerName: "Role",
        flex: 1,
        minWidth: 160,
        renderCell: ({ row }) => {
          const { label, isUnknown } = resolveAssignmentRole(row, rolesById);
          return isUnknown ? (
            <Tooltip title={`role_id: ${row.role_id}`}>
              <span style={{ fontSize: "12px", color: "#b45309", fontWeight: 600 }}>⚠ {label}</span>
            </Tooltip>
          ) : (
            <span style={{ fontSize: "13px", color: "#374151" }}>{label}</span>
          );
        },
      },
      {
        field: "org_id",
        headerName: "Organization",
        flex: 1,
        minWidth: 160,
        renderCell: ({ row }) => {
          const { label, isDerived } = getOrganizationDisplay(row, orgsById);
          return (
            <span style={{ fontSize: "12px", color: isDerived ? "#9ca3af" : "#374151", fontStyle: isDerived ? "italic" : "normal" }}>
              {label}
            </span>
          );
        },
      },
      {
        field: "is_active",
        headerName: "Status",
        width: 100,
        renderCell: ({ row }) => <StatusChip isActive={!!row.is_active} />,
      },
      {
        field: "assigned_by",
        headerName: "Assigned by",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => <span style={{ fontSize: "12px", color: "#6b7280" }}>{row.assigned_by || "—"}</span>,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 150,
        getActions: ({ row }) => {
          // Role hierarchy applies to every row-level action, not just
          // creating/switching a role in the drawers — someone who can
          // technically update/delete assignments (via auth:role-assignments:*)
          // still can't touch a row whose role outranks their own. Shown
          // disabled (not hidden) with a tooltip, same convention as the
          // permission-based disabling right next to it.
          const rowRole = rolesById.get(row.role_id);
          const rowRoleOutranksMe = !canManageRole(rowRole, myMaxPriority);
          const hierarchyTitle = "You cannot manage an assignment whose role is higher than your own.";

          const canEdit = canUpdate && !rowRoleOutranksMe;
          const canToggle = canUpdate && !rowRoleOutranksMe;
          const canRemove = canDelete && !rowRoleOutranksMe;

          return [
            <GridActionsCellItem
              key="edit"
              icon={<EditOutlined fontSize="small" sx={{ color: canEdit ? "#6b7280" : "#d1d5db" }} />}
              label="Edit assignment"
              showInMenu={false}
              disabled={!canEdit}
              title={
                !canUpdate
                  ? `Requires ${PERMISSIONS.ROLE_ASSIGNMENTS_UPDATE} permission`
                  : rowRoleOutranksMe
                    ? hierarchyTitle
                    : "Edit assignment"
              }
              onClick={() => setEditAssignment(row)}
            />,
            <GridActionsCellItem
              key="toggle-active"
              icon={
                row.is_active ? (
                  <PauseCircleOutlineOutlined fontSize="small" sx={{ color: canToggle ? "#6b7280" : "#d1d5db" }} />
                ) : (
                  <PlayCircleOutlineOutlined fontSize="small" sx={{ color: canToggle ? "#6b7280" : "#d1d5db" }} />
                )
              }
              label={row.is_active ? "Deactivate assignment" : "Reactivate assignment"}
              showInMenu={false}
              disabled={!canToggle}
              title={
                !canUpdate
                  ? `Requires ${PERMISSIONS.ROLE_ASSIGNMENTS_UPDATE} permission`
                  : rowRoleOutranksMe
                    ? hierarchyTitle
                    : row.is_active
                      ? "Deactivate assignment"
                      : "Reactivate assignment"
              }
              onClick={() => setDeactivateAssignment(row)}
            />,
            <GridActionsCellItem
              key="delete"
              icon={<DeleteOutlineOutlined fontSize="small" sx={{ color: canRemove ? "#b91c1c" : "#d1d5db" }} />}
              label="Delete assignment"
              showInMenu={false}
              disabled={!canRemove}
              title={
                !canDelete
                  ? `Requires ${PERMISSIONS.ROLE_ASSIGNMENTS_DELETE} permission`
                  : rowRoleOutranksMe
                    ? hierarchyTitle
                    : "Delete assignment"
              }
              onClick={() => setDeleteAssignment(row)}
            />,
          ];
        },
      },
    ],
    [rolesById, orgsById, canUpdate, canDelete, myMaxPriority]
  );

  if (!canRead) {
    return (
      <div className="ecr-ops-content">
        <Alert severity="warning">
          You need the <code>{PERMISSIONS.ROLE_ASSIGNMENTS_READ}</code> permission to view role assignments.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="ecr-ops-page-header am-page-header">
        <div className="am-page-header__text">
          <p className="ecr-ops-page-header__eyebrow">Access Management</p>
          <h1 className="ecr-ops-page-header__title">Role Assignments</h1>
          <p className="ecr-ops-page-header__sub">Manage user and application access.</p>
        </div>
        <div className="am-page-header__actions">
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={refetch} disabled={isFetching} sx={{ color: "#fff" }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <PermissionIconButtonAsText
            hasPermission={canCreate}
            onClick={() => setCreateOpen(true)}
          />
        </div>
      </div>

      <nav className="am-subnav am-subnav--secondary" aria-label="Role Assignments views">
        {VIEWS.map((view) => (
          <button
            key={view.value}
            type="button"
            className={`am-subnav__link${view.value === viewParam ? " active" : ""}`}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.set("view", view.value);
              next.set("page", "1");
              setOpenGroupKey(null);
              setDetailAssignmentId(null);
              setSearchParams(next);
            }}
          >
            {view.label}
          </button>
        ))}
      </nav>

      <div className="ecr-ops-content">
        <div className="am-toolbar">
          <TextField
            size="small"
            placeholder="Search by Email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearchInput("")}
                    sx={{ padding: "2px" }}
                  >
                    <CloseIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 260, "& .MuiOutlinedInput-root": { fontSize: "13px", background: "#fff", borderRadius: "8px" } }}
          />

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="role-assignments-type-filter-label">Type</InputLabel>
            <Select
              labelId="role-assignments-type-filter-label"
              label="Type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="application">Application</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="role-assignments-role-filter-label">Role</InputLabel>
            <Select
              labelId="role-assignments-role-filter-label"
              label="Role"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {(rolesData?.roles || []).map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.role_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="role-assignments-status-filter-label">Status</InputLabel>
            <Select
              labelId="role-assignments-status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Button size="small" onClick={handleClearFilters} sx={{ textTransform: "none" }}>
              Clear filters
            </Button>
          )}

          <Box sx={{ flex: 1 }} />
        </div>

        {/* isLoading only covers the very first fetch — a later refetch
            (search-as-you-type, changing a filter, paging) only flips
            isFetching, which previously showed no loading feedback at all. */}
        {isFetching && !isLoading && !isGroupByMismatched && (
          <LinearProgress sx={{ mb: 1, "& .MuiLinearProgress-bar": { backgroundColor: "#86BC25" } }} />
        )}

        {isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={refetch}>
                Retry
              </Button>
            }
          >
            Failed to load role assignments.
          </Alert>
        ) : isLoading || isGroupByMismatched ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 24px", background: "#fff", border: "1px solid #DCE3EA", borderRadius: "8px" }}>
            <CircularProgress sx={{ color: "#86BC25" }} />
            <p style={{ color: "#9ca3af", fontSize: "13px", marginTop: 12 }}>Loading role assignments…</p>
          </Box>
        ) : groups.length === 0 ? (
          <Box sx={{ textAlign: "center", padding: "32px 0" }}>
            <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: hasActiveFilters ? 0 : 12 }}>
              {hasActiveFilters ? "No role assignments match the current filters." : "No role assignments yet."}
            </p>
            {!hasActiveFilters && canCreate && (
              <Button
                variant="contained"
                startIcon={<AddOutlined />}
                onClick={() => setCreateOpen(true)}
                sx={{ textTransform: "none", backgroundColor: "#86BC25", "&:hover": { backgroundColor: "#76a621" } }}
              >
                Assign role
              </Button>
            )}
          </Box>
        ) : (
          <>
            <div className="am-grid-wrap" style={{ height: Math.min(600, 120 + groups.length * 64) }}>
              <DataGrid
                rows={groups.map((group) => ({ id: String(group.group), group }))}
                columns={groupBy === "email" ? userListColumns : roleListColumns}
                rowHeight={64}
                disableVirtualization
                hideFooterPagination
                hideFooterSelectedRowCount
                onRowClick={(params) => setOpenGroupKey(params.id)}
                sx={{
                  border: "none",
                  fontSize: "13px",
                  "& .MuiDataGrid-row": { cursor: "pointer" },
                  "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F8FAFC", borderBottom: "1px solid #DCE3EA" },
                  "& .MuiDataGrid-columnHeaderTitle": { color: "#111827", fontSize: "12px", fontWeight: 700 },
                  "& .MuiDataGrid-row:hover": { backgroundColor: "#F8FAFC !important" },
                }}
              />
            </div>
            <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: "13px", color: "#6b7280" }}>
                {groups.length} group{groups.length === 1 ? "" : "s"} · {total} total assignment{total === 1 ? "" : "s"}
                {nPages > 1 && ` · Page ${pageParam} of ${nPages}`}
              </div>
              {nPages > 1 && <Pagination nPages={nPages} currentPage={pageParam} setCurrentPage={setPage} />}
            </div>
          </>
        )}
      </div>

      <AssignRoleDrawer
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateInitialEmail("");
          setCreateInitialRoleId("");
          if (returnToGroupKey !== null) {
            setOpenGroupKey(returnToGroupKey);
            setReturnToGroupKey(null);
          }
        }}
        roles={assignableRoles}
        initialEmail={createInitialEmail}
        initialRoleId={createInitialRoleId}
        existingAssignments={groups.flatMap((g) => g.role_assignments || [])}
        onCreated={() => {
          setCreateOpen(false);
          setCreateInitialEmail("");
          setCreateInitialRoleId("");
          refetch();
          if (returnToGroupKey !== null) {
            setOpenGroupKey(returnToGroupKey);
            setReturnToGroupKey(null);
          }
        }}
      />

      <EditAssignmentDrawer
        assignment={editAssignment}
        roles={assignableRoles}
        onClose={() => setEditAssignment(null)}
        onSaved={() => setEditAssignment(null)}
      />

      <DeactivateConfirmDialog assignment={deactivateAssignment} onClose={() => setDeactivateAssignment(null)} />

      <DeleteAssignmentDialog
        assignment={deleteAssignment}
        rolesById={rolesById}
        onClose={() => setDeleteAssignment(null)}
        onDeleted={(assignment) => {
          // Only arms the auto-close check above for the group the deleted
          // assignment actually belonged to — a delete from within a
          // DIFFERENT group's modal (not currently possible today, since
          // this dialog is only reachable from the currently-open group's
          // own grid, but kept explicit rather than assumed) must not close
          // an unrelated open modal.
          const groupKey = groupBy === "role" ? assignment.role_id : assignment.email;
          if (groupKey === openGroupKey) {
            pendingDeleteGroupKeyRef.current = groupKey;
          }
        }}
      />

      <Dialog open={!!openGroupKey} onClose={closeGroupModal} maxWidth="lg" fullWidth>
        {isOpenGroupLoading || !openGroup ? (
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 24px" }}>
              <CircularProgress sx={{ color: "#86BC25" }} />
              <p style={{ color: "#9ca3af", fontSize: "13px", marginTop: 12 }}>Loading assignments…</p>
            </Box>
          </DialogContent>
        ) : (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontSize: "16px", fontWeight: 700 }}>
                {groupBy === "role" ? openGroup.role_name || rolesById[openGroup.group]?.role_name || openGroup.group : openGroup.group}
              </span>
              <span style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 400 }}>
                {openGroup.role_assignments.length} assignment{openGroup.role_assignments.length === 1 ? "" : "s"}
              </span>
              <Box sx={{ flex: 1 }} />
              {canCreate && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddOutlined fontSize="small" />}
                  onClick={() => handleAssignFromGroup(openGroup)}
                  sx={{ textTransform: "none", backgroundColor: "#86BC25", "&:hover": { backgroundColor: "#76a621" } }}
                >
                  Assign role
                </Button>
              )}
              <IconButton size="small" onClick={closeGroupModal} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {groupBy === "role" ? (
                <div className="am-grid-wrap" style={{ height: Math.min(600, 88 + openGroup.role_assignments.length * 60) }}>
                  <DataGrid
                    rows={openGroup.role_assignments}
                    columns={columns.filter((c) => c.field !== "role_id")}
                    rowHeight={60}
                    disableVirtualization
                    hideFooterPagination
                    hideFooterSelectedRowCount
                    onRowClick={(params) => setDetailAssignmentId(params.row.id)}
                    sx={{
                      border: "none",
                      fontSize: "13px",
                      "& .MuiDataGrid-row": { cursor: "pointer" },
                      "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F8FAFC", borderBottom: "1px solid #DCE3EA" },
                      "& .MuiDataGrid-columnHeaderTitle": { color: "#111827", fontSize: "12px", fontWeight: 700 },
                    }}
                  />
                </div>
              ) : (
                // One collapsible section per role instead of a single flat
                // table — a prolific org owner's dozens of per-org rows no
                // longer bury a rare role (e.g. Enterprise Admin) at the
                // bottom of a long scroll. Cross-org roles sort first (see
                // roleSections above) and get a shield icon so they stand
                // out even collapsed.
                roleSections.map((section) => (
                  <Accordion
                    key={section.roleId}
                    expanded={expandedRoleSection === section.roleId}
                    onChange={(_, isExpanded) => setExpandedRoleSection(isExpanded ? section.roleId : null)}
                    disableGutters
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px !important",
                      mb: 1,
                      "&:before": { display: "none" },
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {section.isCrossOrg && (
                          <Tooltip title="Cross-org role — applies across every organization">
                            <ShieldOutlined sx={{ fontSize: 18, color: "#86BC25" }} />
                          </Tooltip>
                        )}
                        <span style={{ fontSize: "14px", fontWeight: 700, color: "#111827" }}>{section.label}</span>
                        <Chip
                          label={section.assignments.length}
                          size="small"
                          sx={{ fontSize: "11px", fontWeight: 700, height: 20, background: "#F3F4F6", color: "#374151" }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <div className="am-grid-wrap" style={{ height: Math.min(500, 88 + section.assignments.length * 60) }}>
                        <DataGrid
                          rows={section.assignments}
                          columns={columns.filter((c) => c.field !== "assignee" && c.field !== "role_id")}
                          rowHeight={60}
                          disableVirtualization
                          hideFooterPagination
                          hideFooterSelectedRowCount
                          onRowClick={(params) => setDetailAssignmentId(params.row.id)}
                          sx={{
                            border: "none",
                            fontSize: "13px",
                            "& .MuiDataGrid-row": { cursor: "pointer" },
                            "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F8FAFC", borderBottom: "1px solid #DCE3EA" },
                            "& .MuiDataGrid-columnHeaderTitle": { color: "#111827", fontSize: "12px", fontWeight: 700 },
                          }}
                        />
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog open={!!detailAssignmentId} onClose={() => setDetailAssignmentId(null)} maxWidth="xs" fullWidth>
        {detailAssignment && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontSize: "16px", fontWeight: 700 }}>Assignment details</span>
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" onClick={() => setDetailAssignmentId(null)} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {(() => {
                const { role, label: roleLabel, isUnknown } = resolveAssignmentRole(detailAssignment, rolesById);
                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <DetailField label="Status">
                      <StatusChip isActive={!!detailAssignment.is_active} />
                    </DetailField>
                    <DetailField label="Role">
                      {isUnknown ? (
                        <span style={{ fontSize: "13px", color: "#b45309", fontWeight: 600 }}>⚠ {roleLabel}</span>
                      ) : (
                        <Box>
                          <p style={{ fontSize: "13px", fontWeight: 700, color: "#111827", margin: 0 }}>{roleLabel}</p>
                          {role?.description && (
                            <p style={{ fontSize: "12px", color: "#6b7280", margin: "2px 0 0" }}>{role.description}</p>
                          )}
                        </Box>
                      )}
                    </DetailField>
                    <DetailField label="Assigned by" value={detailAssignment.assigned_by} />
                    <DetailField label="Created" value={detailAssignment.created_at} />
                    <DetailField label="Updated" value={detailAssignment.updated_at} />
                  </Box>
                );
              })()}
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
};

// Read-only label/value row for the assignment detail modal. `value` for
// plain text, or `children` for a richer node (e.g. a StatusChip).
const DetailField = ({ label, value, children }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
    <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: 600 }}>{label}</span>
    {children || <span style={{ fontSize: "13px", color: "#111827" }}>{value || "—"}</span>}
  </Box>
);

// Small local wrapper so the primary "Assign role" text button follows the same
// disabled+tooltip convention as the icon actions, without forcing every text
// button in the app through PermissionIconButton's icon-button-shaped API.
const PermissionIconButtonAsText = ({ hasPermission, onClick }) => (
  <Tooltip title={hasPermission ? "" : `Requires ${PERMISSIONS.ROLE_ASSIGNMENTS_CREATE} permission`}>
    <span>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddOutlined />}
        onClick={onClick}
        disabled={!hasPermission}
        sx={{ textTransform: "none", backgroundColor: "#86BC25", "&:hover": { backgroundColor: "#76a621" } }}
      >
        Assign role
      </Button>
    </span>
  </Tooltip>
);

export default RoleAssignmentsPage;
